import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Floating, draggable basic calculator, styled to match the exam app:
 * navy header (same as the sidebar), green accents (same green used for
 * the timer / question numbers / the calculator FAB), coral clear/back
 * (same coral as the Submit button), soft-rounded white card.
 *
 * Usage:
 *   const [calcOpen, setCalcOpen] = useState(false);
 *   ...
 *   <button onClick={() => setCalcOpen(true)}>Calculator</button>
 *   {calcOpen && <BasicCalculator onClose={() => setCalcOpen(false)} />}
 */

// Design tokens pulled from the app's existing palette.
const COLORS = {
  navy: "#1b2a4a",
  green: "#198754",
  greenSoft: "#e6f4ec",
  coral: "#e2536b",
  coralSoft: "#fdecef",
  blue: "#0d6efd",
  textDark: "#1f2937",
  muted: "#6b7280",
  bgLight: "#f4f6f9",
  border: "#e5e9f0",
};

function evaluateExpression(rawExpr) {
  if (!rawExpr || !rawExpr.trim()) return 0;

  let expr = rawExpr
    .replace(/×/g, "*")
    .replace(/÷/g, "/")
    .replace(/%/g, "/100");

  // Only digits, whitespace, and basic arithmetic punctuation are allowed —
  // never anything that could run arbitrary code.
  if (!/^[0-9+\-*/().\s]*$/.test(expr)) {
    throw new Error("Invalid character in expression");
  }

  // eslint-disable-next-line no-new-func
  const fn = new Function(`"use strict"; return (${expr});`);
  const result = fn();
  if (typeof result !== "number" || !isFinite(result)) {
    throw new Error("Math error");
  }
  return result;
}

const BUTTON_ROWS = [
  [
    { label: "AC", type: "clear" },
    { label: "⌫", type: "back" },
    { label: "%", type: "input", value: "%" },
    { label: "÷", type: "input", value: "÷" },
  ],
  [
    { label: "7", type: "input", value: "7" },
    { label: "8", type: "input", value: "8" },
    { label: "9", type: "input", value: "9" },
    { label: "×", type: "input", value: "×" },
  ],
  [
    { label: "4", type: "input", value: "4" },
    { label: "5", type: "input", value: "5" },
    { label: "6", type: "input", value: "6" },
    { label: "-", type: "input", value: "-" },
  ],
  [
    { label: "1", type: "input", value: "1" },
    { label: "2", type: "input", value: "2" },
    { label: "3", type: "input", value: "3" },
    { label: "+", type: "input", value: "+" },
  ],
  [
    { label: "0", type: "input", value: "0", wide: true },
    { label: ".", type: "input", value: "." },
    { label: "=", type: "equals" },
  ],
];

export default function BasicCalculator({ onClose }) {
  const [expr, setExpr] = useState("");
  const [display, setDisplay] = useState("0");
  const [error, setError] = useState(false);
  const [justEvaluated, setJustEvaluated] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });

  const panelRef = useRef(null);
  const dragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

  // Place the panel near the bottom-right on first open, above the FAB.
  useEffect(() => {
    const w = 260;
    const h = 400;
    setPosition({
      x: Math.max(16, window.innerWidth - w - 24),
      y: Math.max(16, window.innerHeight - h - 100),
    });
  }, []);

  const onDragStart = useCallback((e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = {
      dragging: true,
      offsetX: clientX - rect.left,
      offsetY: clientY - rect.top,
    };
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.dragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const w = panelRef.current?.offsetWidth || 260;
      const h = panelRef.current?.offsetHeight || 400;
      const x = Math.min(Math.max(0, clientX - dragRef.current.offsetX), window.innerWidth - w);
      const y = Math.min(Math.max(0, clientY - dragRef.current.offsetY), window.innerHeight - h);
      setPosition({ x, y });
    };
    const onUp = () => {
      dragRef.current.dragging = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const appendToken = (token) => {
    setError(false);
    setExpr((prev) => {
      const base = justEvaluated && /^[0-9.]/.test(token) ? "" : prev;
      return base + token;
    });
    setJustEvaluated(false);
  };

  const clearAll = () => {
    setExpr("");
    setDisplay("0");
    setError(false);
    setJustEvaluated(false);
  };

  const backspace = () => {
    setError(false);
    setExpr((prev) => prev.slice(0, -1));
    setJustEvaluated(false);
  };

  const runEquals = () => {
    try {
      const result = evaluateExpression(expr);
      const rounded = Math.round(result * 1e10) / 1e10;
      setDisplay(String(rounded));
      setExpr(String(rounded));
      setError(false);
      setJustEvaluated(true);
    } catch {
      setDisplay("Error");
      setError(true);
      setJustEvaluated(true);
    }
  };

  const handleButton = (btn) => {
    if (btn.type === "clear") return clearAll();
    if (btn.type === "back") return backspace();
    if (btn.type === "equals") return runEquals();
    if (btn.type === "input") return appendToken(btn.value);
  };

  // Keep the live display in sync with the expression while typing.
  useEffect(() => {
    if (justEvaluated) return;
    setDisplay(expr.length ? expr : "0");
  }, [expr, justEvaluated]);

  const handleKeyDown = (e) => {
    const key = e.key;
    if (/^[0-9.+\-()]$/.test(key)) {
      e.preventDefault();
      appendToken(key);
    } else if (key === "*") {
      e.preventDefault();
      appendToken("×");
    } else if (key === "/") {
      e.preventDefault();
      appendToken("÷");
    } else if (key === "Enter" || key === "=") {
      e.preventDefault();
      runEquals();
    } else if (key === "Backspace") {
      e.preventDefault();
      backspace();
    } else if (key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  };

  if (position.x === null) return null;

  return (
    <div
      ref={panelRef}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        width: 260,
        zIndex: 1050,
        background: "#ffffff",
        borderRadius: 16,
        boxShadow: "0 10px 28px rgba(27, 42, 74, 0.22)",
        border: `1px solid ${COLORS.border}`,
        overflow: "hidden",
        fontFamily: "inherit",
        userSelect: "none",
      }}
    >
      {/* Header / drag handle — same navy as the sidebar */}
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        style={{
          cursor: "move",
          background: COLORS.navy,
          color: "#fff",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.2, display: "flex", alignItems: "center", gap: 8 }}>
          <i className="bi bi-calculator-fill" style={{ color: COLORS.green }} />
          Calculator
        </span>
        <button
          onClick={onClose}
          aria-label="Close calculator"
          style={{
            background: "transparent",
            border: "none",
            color: "#cbd5e1",
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* Display */}
      <div style={{ padding: "14px 16px 8px", background: COLORS.bgLight }}>
        {error && (
          <div style={{ fontSize: 11, color: COLORS.coral, marginBottom: 4, textAlign: "right", fontWeight: 600 }}>
            Invalid expression
          </div>
        )}
        <div
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: 28,
            textAlign: "right",
            color: error ? COLORS.coral : COLORS.textDark,
            background: "#ffffff",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            padding: "10px 14px",
            minHeight: 48,
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          {display}
        </div>
      </div>

      {/* Button grid */}
      <div style={{ padding: "10px 14px 16px", background: COLORS.bgLight, display: "flex", flexDirection: "column", gap: 8 }}>
        {BUTTON_ROWS.map((row, rowIdx) => (
          <div key={rowIdx} style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {row.map((btn, i) => {
              const isEquals = btn.type === "equals";
              const isClearOrBack = btn.type === "clear" || btn.type === "back";
              const isOperator = btn.type === "input" && ["÷", "×", "-", "+", "%"].includes(btn.value);

              let bg = "#ffffff";
              let color = COLORS.textDark;
              let border = COLORS.border;

              if (isEquals) {
                bg = COLORS.green;
                color = "#ffffff";
                border = COLORS.green;
              } else if (isClearOrBack) {
                bg = COLORS.coralSoft;
                color = COLORS.coral;
                border = "#f6d3da";
              } else if (isOperator) {
                bg = COLORS.greenSoft;
                color = COLORS.green;
                border = "#cdeada";
              }

              return (
                <button
                  key={i}
                  onClick={() => handleButton(btn)}
                  style={{
                    gridColumn: btn.wide ? "span 2" : undefined,
                    padding: "12px 0",
                    fontSize: 16,
                    fontWeight: isEquals || isOperator || isClearOrBack ? 700 : 500,
                    borderRadius: 10,
                    border: `1px solid ${border}`,
                    background: bg,
                    color,
                    cursor: "pointer",
                    transition: "filter 0.15s ease",
                  }}
                >
                  {btn.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}